"use client";

import { useState, useEffect, useRef, useTransition } from "react";
import { useTranslations } from "next-intl";
import { useToast } from "@/components/ui/Toast";
import { BookmarkSimple } from "@phosphor-icons/react/dist/ssr";
import { createClient } from "@/lib/supabase/client";
import { toggleSavedProAction } from "@/lib/actions/saved-pros";

interface Props {
  slug: string;
  name: string;
  professionalId: string;
}

export default function BookmarkButton({ slug, name, professionalId }: Props) {
  const t = useTranslations("bookmark");
  const { error: toastError } = useToast();
  const [saved, setSaved] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [isPending, startTransition] = useTransition();
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const supabase = createClient();

    async function init() {
      const { data: { user } } = await supabase.auth.getUser();
      setUserId(user?.id ?? null);

      if (user) {
        // Check DB for saved state
        const { data } = await supabase
          .from("user_saved_professionals")
          .select("professional_id")
          .eq("user_id", user.id)
          .eq("professional_id", professionalId)
          .maybeSingle();
        setSaved(!!data);
      } else {
        setSaved(false);
      }
    }

    init();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id ?? null);
      if (!session?.user) setSaved(false);
    });

    return () => subscription.unsubscribe();
  }, [slug, professionalId]);

  const toggle = () => {
    if (!userId) return;
    startTransition(async () => {
      try {
        const nextSaved = await toggleSavedProAction(professionalId);
        setSaved(nextSaved);
      } catch {
        toastError(t("error"));
      }
    });
  };

  const showTooltip = () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    setTooltipVisible(true);
  };

  const hideTooltip = () => {
    hideTimer.current = setTimeout(() => setTooltipVisible(false), 120);
  };

  if (!userId) {
    return (
      <span
        className="relative inline-flex"
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
      >
        <button
          disabled
          aria-label={t("loginToSaveAria", { name })}
          aria-describedby={`bookmark-tooltip-${slug}`}
          className="inline-flex cursor-not-allowed items-center gap-8 rounded-control border border-navy/10 px-13 py-8 text-xs font-semibold uppercase tracking-wider text-navy/30"
        >
          <BookmarkSimple size={14} weight="regular" aria-hidden="true" />
          {t("save")}
        </button>
        <span
          id={`bookmark-tooltip-${slug}`}
          role="tooltip"
          style={{
            opacity: tooltipVisible ? 1 : 0,
            transform: tooltipVisible ? "translateY(0)" : "translateY(4px)",
            transition: "opacity 140ms ease, transform 140ms ease",
            pointerEvents: "none",
          }}
          className="absolute bottom-[calc(100%+6px)] left-1/2 z-50 w-max -translate-x-1/2 rounded-control bg-navy px-8 py-5 text-[0.65rem] font-medium text-white shadow-e3"
        >
          {t("loginTooltip")}
          <span
            aria-hidden="true"
            className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-navy"
          />
        </span>
      </span>
    );
  }

  return (
    <button
      onClick={toggle}
      disabled={isPending}
      aria-label={saved ? t("removeAria", { name }) : t("saveAria", { name })}
      aria-pressed={saved}
      className={[
        "inline-flex items-center gap-8 rounded-control border px-13 py-8 text-xs font-semibold uppercase tracking-wider transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold disabled:opacity-60",
        saved
          ? "border-gold bg-gold/10 text-navy"
          : "border-navy/15 text-navy hover:border-navy/40",
      ].join(" ")}
    >
      <BookmarkSimple
        size={14}
        weight={saved ? "fill" : "regular"}
        aria-hidden="true"
        className={saved ? "text-gold" : ""}
      />
      {saved ? t("saved") : t("save")}
    </button>
  );
}
